/**
 * Derivation of the outline's row list from the transcript EventNode tree.
 */

import { useMemo } from "react";

import { flatTree } from "../transform/flatten";
import { EventNode } from "../types";

import {
  collapseScoring,
  collapseTurns,
  makeTurns,
  noScorerChildren,
  outlineFilterVisitors,
} from "./tree-visitors";
import { findNearestOutlineAbove } from "./useOutlineScrollSync";

/**
 * Resolve a selected event id to the outline row that contains it.
 *
 * A collapsed "N turns" row reuses its first turn's model-event id, so a
 * selection on a later turn in the group matches no row by identity; fall back
 * to the nearest outline row at or above the selection.
 */
export const resolveOutlineSelection = (
  selectedOutlineId: string | null | undefined,
  allNodesList: EventNode[],
  outlineNodeList: EventNode[]
): string | null => {
  if (!selectedOutlineId) return null;
  const outlineIds = new Set(outlineNodeList.map((node) => node.id));
  if (outlineIds.has(selectedOutlineId)) return selectedOutlineId;
  return (
    findNearestOutlineAbove(selectedOutlineId, allNodesList, outlineIds)?.id ??
    null
  );
};

/**
 * Build the outline's row list: flatten the tree with the outline's
 * visibility visitors, then group model/tool runs into turns and collapse
 * consecutive turns and scoring events.
 */
export const buildOutlineNodeList = (
  eventNodes: EventNode[],
  collapsedIds: Record<string, boolean>
): EventNode[] => {
  const nodeList = flatTree(eventNodes, collapsedIds, [
    ...outlineFilterVisitors(),
    noScorerChildren(),
  ]);

  return collapseScoring(collapseTurns(makeTurns(nodeList)));
};

export interface OutlineNodes {
  /** Rows displayed in the outline. */
  outlineNodeList: EventNode[];
  /** Full flattened node list (unfiltered), for scroll tracking. */
  allNodesList: EventNode[];
}

export const useOutlineNodes = (
  eventNodes: EventNode[],
  collapsedIds: Record<string, boolean>
): OutlineNodes => {
  const outlineNodeList = useMemo(
    () => buildOutlineNodeList(eventNodes, collapsedIds),
    [eventNodes, collapsedIds]
  );

  const allNodesList = useMemo(() => {
    return flatTree(eventNodes, null);
  }, [eventNodes]);

  return { outlineNodeList, allNodesList };
};
